import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCategories, fetchFeaturedVendors, fetchCities, fetchAreasByCity } from '../services/api';
import { saveDeductedLocation, getDeductedLocation } from '../utils/locationStorage';
import { API_CONFIG } from '../config/api.config';

import CategorySection from '../components/CategorySection';
import {
    Box,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Grid,
    Stack,
    Paper,
    Popper,
    List,
    ListItemButton,
    CircularProgress,
    Card,
    Container
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Typography from '@mui/material/Typography';
// Icons for the new benefits section
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ShowChartIcon from '@mui/icons-material/ShowChart';

const Home = () => {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [vendors, setVendors] = useState([]);
    const [vendorLoading, setVendorLoading] = useState(true);

    // Search form states
    const [searchCategory, setSearchCategory] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [selectedArea, setSelectedArea] = useState('');
    const [cities, setCities] = useState([]);
    const [areas, setAreas] = useState([]);
    const [userLocation, setUserLocation] = useState('');

    // Search suggestions states
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const debounceRef = useRef(null);
    const searchInputRef = useRef(null);

    useEffect(() => {
        const loadCategories = async () => {
            try {
                setLoading(true);
                const response = await fetchCategories();
                setCategories(response);
                setError(null);
            } catch (err) {
                console.error('Failed to load categories:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadCategories();
    }, []);

    useEffect(() => {
        const loadVendors = async () => {
            setVendorLoading(true);

            if ("geolocation" in navigator) {
                navigator.geolocation.getCurrentPosition(
                    async (pos) => {
                        const data = await fetchFeaturedVendors({
                            lat: pos.coords.latitude,
                            lng: pos.coords.longitude,
                            limit: 3
                        });

                        setVendors(formatVendors(data));
                        setVendorLoading(false);
                    },
                    async () => {
                        const data = await fetchFeaturedVendors({ limit: 3 });
                        setVendors(formatVendors(data));
                        setVendorLoading(false);
                    }
                );
            } else {
                const data = await fetchFeaturedVendors({ limit: 3 });
                setVendors(formatVendors(data));
                setVendorLoading(false);
            }
        };

        loadVendors();
    }, []);

    // Load cities and areas for search form
    useEffect(() => {
        const loadLocations = async () => {
            try {
                const citiesData = await fetchCities();
                setCities(citiesData);
            } catch (err) {
                console.error('Failed to load cities:', err);
            }
        };
        loadLocations();
    }, []);

    // Get user location and auto-select city
    const getUserLocation = (citiesData) => {
        // Check for previously saved location first
        const saved = getDeductedLocation();
        if (saved && saved.city_id) {
            console.log('📍 Using saved location from localStorage:', saved.city_name);
            setUserLocation(saved.city_name);
            
            // Find and set the matching city
            if (citiesData && citiesData.length > 0) {
                const matchingCity = citiesData.find(c => c.id === saved.city_id);
                if (matchingCity) {
                    console.log('✅ Auto-selecting city:', matchingCity.name);
                    setSelectedCity(matchingCity.id);
                }
            }
            return;
        }

        // If no saved location, try geolocation API
        if (!navigator.geolocation) {
            console.warn('⚠️ Geolocation not available');
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const { latitude: lat, longitude: lng } = pos.coords;
                try {
                    console.log(`🧪 Detecting location with coords: lat=${lat}, lng=${lng}`);
                    
                    // Call backend location detection endpoint
                    const response = await fetch(`${API_CONFIG.BASE_URL}/detect-location?lat=${lat}&lng=${lng}`);
                    const data = await response.json();
                    console.log('✅ Location detected:', data);

                    if (data.city_id && data.city_name) {
                        // Save deducted location with timestamp
                        saveDeductedLocation({
                            city_id: data.city_id,
                            city_name: data.city_name,
                            area_id: data.area_id,
                            area_name: data.area_name,
                            lat,
                            lng
                        });
                        
                        setUserLocation(data.city_name);
                        
                        // Auto-apply filters with city
                        if (citiesData && citiesData.length > 0) {
                            const matchingCity = citiesData.find(c => c.id === data.city_id);
                            if (matchingCity) {
                                console.log('✅ Auto-selecting detected city:', matchingCity.name);
                                setSelectedCity(matchingCity.id);
                            } else {
                                console.warn('⚠️ No matching city found for city_id:', data.city_id);
                            }
                        }
                    }
                } catch (error) {
                    console.error('❌ Location detection error:', error);
                }
            },
            (error) => {
                console.warn('⚠️ Geolocation failed:', error.message);
            }
        );
    };

    // Auto-select city when cities load
    useEffect(() => {
        if (cities.length > 0 && !selectedCity) {
            getUserLocation(cities);
        }
    }, [cities]);

    // Load areas when city is selected
    useEffect(() => {
        if (selectedCity) {
            const loadAreas = async () => {
                try {
                    const areasData = await fetchAreasByCity(selectedCity);
                    setAreas(areasData);
                    setSelectedArea(''); // Reset area when city changes
                } catch (err) {
                    console.error('Failed to load areas:', err);
                }
            };
            loadAreas();
        }
    }, [selectedCity]);

    // Fetch search suggestions
    const fetchSuggestions = async (q) => {
        if (!q.trim()) {
            setShowSuggestions(false);
            return;
        }
        try {
            setSuggestionsLoading(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/search/suggestions?q=${encodeURIComponent(q)}`);
            const data = await response.json();
            const apiSuggestions = (data.suggestions || []).map(s => typeof s === 'object' ? s.text : s);
            // Add user's typed text as first suggestion
            const allSuggestions = [q, ...apiSuggestions];
            setSuggestions(allSuggestions);
            setShowSuggestions(true);
            setSuggestionsLoading(false);
        } catch (err) {
            console.error('Failed to fetch suggestions:', err);
            setSuggestionsLoading(false);
        }
    };

    // Handle search input with debounce
    const handleSearchInput = (value) => {
        setSearchCategory(value);
        clearTimeout(debounceRef.current);
        if (value.trim().length > 1) {
            debounceRef.current = setTimeout(() => fetchSuggestions(value.trim()), 400);
        } else {
            setShowSuggestions(false);
        }
    };

    // Select suggestion - just set the text, don't navigate
    const handleSuggestionClick = (suggestion) => {
        setSearchCategory(suggestion);
        setShowSuggestions(false);
    };

    const formatVendors = (vendors) => {
        return vendors.map(v => {
            const firstService = v.services?.[0];

            return {
                id: v.id,
                name: v.name,
                service: v.services?.map(s => s.name).join(' · ') || 'Service Provider',
                location: `${v.area?.name || ''}, ${v.city?.name || ''}`,
                rating: v.rating || 4.5,
                reviews: v.reviews?.length || 0,
                avatar: v.profile_picture
                    ? v.profile_picture
                    : firstService?.category?.icon || '👤',
                isImage: !!v.profile_picture,
                verified: true,
            };
        });
    };

    // Handle search submission
    const handleSearch = () => {
        const params = new URLSearchParams();

        if (selectedCategory) {
            params.append('category_id', selectedCategory);
        }
        if (selectedCity) {
            params.append('city_id', selectedCity);
        }
        if (selectedArea) {
            params.append('area_id', selectedArea);
        }
        if (searchCategory) {
            params.append('search', searchCategory);
        }

        navigate(`/services?${params.toString()}`);
    };

    return (
        <div>
            {/* HERO */}
            <div className="hero">
                <div className="hero-grid">
                    <div className="hero-left">
                        <div className="hero-eyebrow">
                            <div className="hero-dot"></div>
                            Live in 120+ neighbourhoods
                        </div>
                        <h1 className="hero-title">
                            Find Trusted<br />
                            <span>Local Services</span><br />
                            Near You
                        </h1>
                        <p className="hero-desc">
                            Connect with verified electricians, plumbers, tutors, and local vendors — right in your neighbourhood.
                        </p>
                        <div className="hero-actions">
                            <button className="btn btn-accent btn-lg" onClick={() => navigate('/services')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8" />
                                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                                Find Services
                            </button>
                            <button className="btn btn-ghost" style={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }} onClick={() => navigate('/add-listing')}>
                                List Your Service Free →
                            </button>
                        </div>
                        <div className="hero-stats">
                            <div>
                                <div className="hero-stat-value">2,400+</div>
                                <div className="hero-stat-label">Local vendors</div>
                            </div>
                            <div>
                                <div className="hero-stat-value">38</div>
                                <div className="hero-stat-label">Categories</div>
                            </div>
                            <div>
                                <div className="hero-stat-value">120+</div>
                                <div className="hero-stat-label">Areas covered</div>
                            </div>
                        </div>
                    </div>

                    <div className="hero-search-card">
                        <h3>Search near you</h3>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
                            {/* Search Input */}
                            <Box sx={{ position: 'relative' }}>
                                <TextField
                                    ref={searchInputRef}
                                    fullWidth
                                    placeholder="What service do you need?"
                                    value={searchCategory}
                                    onChange={(e) => handleSearchInput(e.target.value)}
                                    onFocus={() => searchCategory.trim().length > 1 && setShowSuggestions(true)}
                                    size="small"
                                    InputProps={{
                                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'rgba(255,255,255,0.6)', fontSize: { xs: '1.2rem', md: 'inherit' } }} />,
                                        endAdornment: suggestionsLoading && <CircularProgress color="inherit" size={20} sx={{ mr: 1, color: 'rgba(255,255,255,0.6)' }} />,
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: 'white',
                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                            border: '1px solid rgba(255,255,255,0.2)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255,255,255,0.15)',
                                            },
                                            '&.Mui-focused': {
                                                backgroundColor: 'rgba(255,255,255,0.15)',
                                                borderColor: 'rgba(255,255,255,0.4)',
                                            },
                                        },
                                        '& .MuiOutlinedInput-input': {
                                            fontSize: { xs: '0.85rem', md: '0.9rem' },
                                        },
                                        '& .MuiOutlinedInput-input::placeholder': {
                                            color: 'rgba(255,255,255,0.45)',
                                            opacity: 1,
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(255,255,255,0.2)',
                                        },
                                    }}
                                />
                                {/* Suggestions Dropdown */}
                                <Popper
                                    open={showSuggestions && suggestions.length > 0}
                                    anchorEl={searchInputRef.current}
                                    placement="bottom-start"
                                    style={{ width: searchInputRef.current?.offsetWidth, zIndex: 1000 }}
                                >
                                    <Paper sx={{ width: '100%', mt: 0.5, backgroundColor: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(10px)' }}>
                                        <List sx={{ py: 0 }}>
                                            {suggestions.map((suggestion, idx) => (
                                                <ListItemButton
                                                    key={idx}
                                                    onClick={() => handleSuggestionClick(suggestion)}
                                                    sx={{
                                                        py: 1,
                                                        px: 2,
                                                        borderBottom: '1px solid rgba(255,255,255,0.1)',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                                        },
                                                        '&:last-child': {
                                                            borderBottom: 'none',
                                                        },
                                                    }}
                                                >
                                                    <Typography sx={{ color: 'white', fontSize: '0.9rem' }}>
                                                        {suggestion}
                                                    </Typography>
                                                </ListItemButton>
                                            ))}
                                        </List>
                                    </Paper>
                                </Popper>
                            </Box>

                            {/* City & Area Selectors */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr' }, gap: { xs: 1, md: 2 } }}>
                                {/* City Select */}
                                <FormControl fullWidth size="small">
                                    <InputLabel sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.8rem', md: 'inherit' } }}>City</InputLabel>
                                    <Select
                                        value={selectedCity}
                                        onChange={(e) => setSelectedCity(e.target.value)}
                                        label="City"
                                        sx={{
                                            color: 'white',
                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                            border: '1px solid rgba(255,255,255,0.2)',
                                            fontSize: { xs: '0.85rem', md: 'inherit' },
                                            '&:hover': {
                                                backgroundColor: 'rgba(255,255,255,0.15)',
                                            },
                                            '&.Mui-focused': {
                                                backgroundColor: 'rgba(255,255,255,0.15)',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(255,255,255,0.2)',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: 'rgba(255,255,255,0.7)',
                                            },
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>Select City</em>
                                        </MenuItem>
                                        {cities.map(city => (
                                            <MenuItem key={city.id} value={city.id}>
                                                {city.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Area Select */}
                                <FormControl fullWidth size="small" disabled={!selectedCity}>
                                    <InputLabel sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.8rem', md: 'inherit' } }}>Area</InputLabel>
                                    <Select
                                        value={selectedArea}
                                        onChange={(e) => setSelectedArea(e.target.value)}
                                        label="Area"
                                        sx={{
                                            color: 'white',
                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                            border: '1px solid rgba(255,255,255,0.2)',
                                            fontSize: { xs: '0.85rem', md: 'inherit' },
                                            '&:hover': {
                                                backgroundColor: 'rgba(255,255,255,0.15)',
                                            },
                                            '&.Mui-focused': {
                                                backgroundColor: 'rgba(255,255,255,0.15)',
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: 'rgba(255,255,255,0.05)',
                                                color: 'rgba(255,255,255,0.4)',
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(255,255,255,0.2)',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: 'rgba(255,255,255,0.7)',
                                            },
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>Select Area</em>
                                        </MenuItem>
                                        {areas.map(area => (
                                            <MenuItem key={area.id} value={area.id}>
                                                {area.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Search Button */}
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleSearch}
                                sx={{
                                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: { xs: '0.9rem', md: '0.95rem' },
                                    py: { xs: 1, md: 1.2 },
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #4338ca 0%, #6d28d9 100%)',
                                    },
                                }}
                            >
                                Search Services
                            </Button>
                        </Box>

                        {/* Quick Tags */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                            {categories.filter(cat => !cat.parent_id).slice(0, 5).map((cat) => (
                                <Paper
                                    key={cat.id}
                                    onClick={() => {
                                        const catId = String(cat.id);
                                        setSelectedCategory(catId);
                                        setTimeout(() => {
                                            const params = new URLSearchParams();
                                            params.append('category_id', catId);
                                            if (selectedCity) params.append('city_id', selectedCity);
                                            if (selectedArea) params.append('area_id', selectedArea);

                                            navigate(`/services?${params.toString()}`);
                                        }, 50);
                                    }}
                                    sx={{
                                        px: 1.5,
                                        py: 0.75,
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                        border: '1px solid rgba(255,255,255,0.15)',
                                        color: 'rgba(255,255,255,0.8)',
                                        fontSize: '0.85rem',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        transition: 'all 0.15s',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.2)',
                                            borderColor: 'rgba(255,255,255,0.3)',
                                            transform: 'translateY(-2px)',
                                        },
                                        borderRadius: '99px',
                                    }}
                                >
                                    {cat.icon || '⭐'} {cat.name}
                                </Paper>
                            ))}
                        </Box>
                    </div>
                </div>
            </div>

            {/* TRUST BAR */}
            <div className="trust-bar">
                <div className="trust-bar-inner">
                    <div className="trust-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                        Verified Vendors
                    </div>
                    <div className="trust-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        </svg>
                        Safe & Trusted
                    </div>
                    <div className="trust-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                        </svg>
                        Instant Contact
                    </div>
                    <div className="trust-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                        </svg>
                        Hyperlocal Discovery
                    </div>
                    <div className="trust-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                        </svg>
                        Free Listings
                    </div>
                </div>
            </div>

            {/* CATEGORIES */}
            <div className="section">
                <div className="section-header">
                    <h2 className="section-title">Browse by Category</h2>
                    <button className="btn btn-secondary btn-sm" onClick={() => navigate('/services')}>All services →</button>
                </div>
                <CategorySection categories={categories} loading={loading} />
            </div>

            {/* CTA BANNER */}
            <div className="section" component="section"
                style={{
                    background: '#ffffff',
                    padding: { xs: '32px 16px', sm: '48px 20px', md: '64px 40px' },
                    borderTop: '1px solid #e5e7eb',
                    borderBottom: '1px solid #e5e7eb', maxWidth: '100%'
                }}>
                <div className="cta-banner">
                    <div className="cta-banner-text">
                        <h2>Are you a local service provider?</h2>
                        <p>List your service for free. Reach thousands of customers in your neighbourhood.</p>
                    </div>
                    <div className="cta-banner-actions">
                        <button className="btn btn-accent btn-lg" onClick={() => navigate('/add-listing')}>List Your Service Free</button>
                    </div>
                </div>
            </div>
            {/* FEATURED VENDORS */}
            <div className="section" style={{ paddingTop: 0, marginTop: 20 }}>
                <div className="section-header">
                    <h2 className="section-title">Featured Vendors</h2>
                </div>
                {vendorLoading ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '300px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <CircularProgress sx={{ mb: 2, color: 'var(--primary)' }} />
                            <p style={{ color: 'var(--text-3)', fontSize: '0.95rem', margin: 0 }}>Loading featured vendors...</p>
                        </div>
                    </div>
                ) : vendors.length > 0 ? (
                    <div className="vendor-grid">
                        {vendors.map((vendor) => (
                            <div key={vendor.id} className="vendor-card" onClick={() => navigate(`/vendor/${vendor.id}`)} style={{ cursor: 'pointer' }}>
                                <div className="vendor-card-header">
                                    <div className="vendor-avatar">
                                        {vendor.isImage ? <img src={vendor.avatar} alt={vendor.name} /> : <span>{vendor.avatar}</span>}
                                    </div>
                                    {vendor.verified && <div className="vendor-badge-verified">✓ Verified</div>}
                                </div>
                                <div className="vendor-card-body">
                                    <div className="vendor-name">{vendor.name}</div>
                                    <div className="vendor-service">{vendor.service}</div>
                                    <div className="vendor-meta">
                                        <div className="vendor-location">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                                            </svg>
                                            {vendor.location}
                                        </div>
                                        <div className="vendor-rating">★ {vendor.rating} <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>({vendor.reviews})</span></div>
                                    </div>
                                    <div className="vendor-actions">
                                        <button className="btn btn-call btn-sm" style={{ flex: 1 }}>📞 Call</button>
                                        <button className="btn btn-whatsapp btn-sm" style={{ flex: 1 }}>💬 WhatsApp</button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p style={{ textAlign: 'center', color: 'var(--text-3)', padding: '48px 20px' }}>No featured vendors available</p>
                )}
            </div>

            {/* ═══ NEW VENDOR GROWTH SECTION ═══ */}
            <Box
                component="section"
                sx={{
                    background: '#ffffff',
                    py: { xs: 4, sm: 6, md: 8 },
                    borderTop: '1px solid #e5e7eb',
                    borderBottom: '1px solid #e5e7eb',
                }}
            >
                <Container maxWidth="lg">
                    <Grid
                        container
                        spacing={{ xs: 4, md: 7 }}
                        alignItems="center"
                    >
                        {/* LEFT CONTENT */}
                        <Grid item xs={12} md={6}>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: 800,
                                    color: 'text.primary',
                                    mb: 2,
                                    lineHeight: 1.2,
                                    fontSize: { xs: '22px', sm: '26px', md: '32px' }
                                }}
                            >
                                Grow Your Business with
                                <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>
                                    NearByService
                                </Box>
                            </Typography>

                            <Typography
                                sx={{
                                    color: 'text.secondary',
                                    mb: 3,
                                    fontSize: { xs: '14px', md: '16px' }
                                }}
                            >
                                Join thousands of local service providers and connect with customers nearby.
                            </Typography>

                             <Stack spacing={2} sx={{ mb: 4 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
                                    {[
                                        { icon: MonetizationOnIcon, title: 'Free Listing', desc: 'No setup or subscription cost' },
                                        { icon: StorefrontIcon, title: 'Local Reach', desc: 'Connect with customers nearby' },
                                        { icon: ShowChartIcon, title: 'Grow Income', desc: 'Increase bookings & visibility' },
                                        { icon: MonetizationOnIcon, title: 'Instant Notifications', desc: 'Get real-time customer inquiries' },
                                        { icon: StorefrontIcon, title: 'Build Trust', desc: 'Get verified badge & customer reviews' },
                                        { icon: ShowChartIcon, title: 'Performance Analytics', desc: 'Track profile views & conversions' },
                                    ].map((item, i) => {
                                        const Icon = item.icon;
                                        const delays = ['0.05s', '0.1s', '0.15s', '0.2s', '0.25s', '0.3s'];
                                        return (
                                            <Box key={i} sx={{ 
                                                display: 'flex', 
                                                gap: 2, 
                                                p: 2, 
                                                borderRadius: 2, 
                                                backgroundColor: 'action.hover',
                                                animation: `slideInUp 0.5s ease-out backwards`,
                                                animationDelay: delays[i],
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    backgroundColor: 'primary.light',
                                                    transform: 'translateY(-4px)',
                                                }
                                            }}>
                                                <Icon sx={{ color: 'primary.main', flexShrink: 0, fontSize: '24px' }} />
                                                <Box>
                                                    <Typography fontWeight={600} sx={{ fontSize: '0.95rem' }}>{item.title}</Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                                        {item.desc}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Stack>
                            {/* ✅ THEME BUTTON */}
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => navigate('/add-listing')}
                                sx={{
                                    borderRadius: '999px',
                                    px: 4,
                                    py: 1.5,
                                    fontWeight: 600
                                }}
                            >
                                List Your Service Free →
                            </Button>
                        </Grid>

                        {/* RIGHT IMAGE */}
                        <Grid item xs={12} md={6}>
                            <Box
                                component="img"
                                src="/image/image5.png"
                                alt="Grow Business"
                                sx={{
                                    display: 'block',
                                    width: '100%',
                                    maxWidth: '100%',
                                    height: { md: '420px', xs: 'auto' },
                                    objectFit: 'cover',
                                    borderRadius: 3,
                                    boxShadow: 3
                                }}
                            />
                        </Grid>
                    </Grid>
                </Container>
            </Box>

        </div>
    );
};

export default Home;